import { useEffect, useRef, useState } from "react"
import navConfig from "../data/nav-config.json"

export function useNavScroll() {
  const [activeSection, setActiveSection] = useState("")
  const [showNav, setShowNav] = useState(false)
  const [isInHeroSection, setIsInHeroSection] = useState(true)
  const [isInDemoSection, setIsInDemoSection] = useState(false)
  const navLockRef = useRef<string | null>(null)

  // Highlight nav items using IntersectionObserver + CSS scroll-margin
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        // Find all intersecting entries and their ratios
        const intersecting = entries
          .filter((e) => {
            if (!e.isIntersecting) return false
            // Hero needs to be at least 40% visible to be considered active
            if (e.target.id === "hero" && e.intersectionRatio < 0.4) return false
            return true
          })
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)

        if (navLockRef.current === null) {
          if (intersecting.length > 0) {
            const firstIntersecting = intersecting[0]
            if (firstIntersecting) {
              const observedId = firstIntersecting.target.id
              setActiveSection(observedId === "hero" ? "" : observedId)
            }
          } else if (window.scrollY < 100) {
            setActiveSection("")
          }
        } else if (window.scrollY < 100) {
          setActiveSection("")
        }
      },
      {
        rootMargin: "-10% 0px -30% 0px",
        threshold: [0, 0.1, 0.3, 0.5, 0.7, 1],
      },
    )

    for (const id of navConfig.sectionIds) {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    }

    // Show/hide nav on scroll top threshold + clear active section when at top
    const handleScrollVisibility = () => {
      const hideThreshold =
        window.innerWidth < 768
          ? navConfig.scrollSettings.hideThresholdMobile
          : navConfig.scrollSettings.hideThresholdDesktop
      const heroThreshold =
        window.innerWidth < 768
          ? navConfig.scrollSettings.heroThresholdMobile
          : navConfig.scrollSettings.heroThresholdDesktop
      const scrollY = window.scrollY

      setShowNav(scrollY >= hideThreshold)
      setIsInHeroSection(scrollY < heroThreshold)

      // Clear active section when near the very top
      if (scrollY < hideThreshold) {
        setActiveSection("")
      }
    }

    window.addEventListener("scroll", handleScrollVisibility, { passive: true })
    handleScrollVisibility()

    return () => {
      io.disconnect()
      window.removeEventListener("scroll", handleScrollVisibility)
    }
  }, [])

  // Listen for demo section visibility
  useEffect(() => {
    const handleDemoVisible = (event: Event) => {
      const customEvent = event as CustomEvent<{ isVisible: boolean }>
      setIsInDemoSection(Boolean(customEvent.detail?.isVisible))
    }
    window.addEventListener("landingDemoSectionVisible", handleDemoVisible)
    return () => window.removeEventListener("landingDemoSectionVisible", handleDemoVisible)
  }, [])

  // Simple scroll to section using CSS scroll-margin
  const scrollToAnchor = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return

    // Immediately update active section for instant visual feedback
    setActiveSection(id)

    // Notify other components when we click Bracket, etc.
    if (id === "bracket") {
      sessionStorage.setItem("bracket:nav-clicked", "true")
      window.dispatchEvent(new CustomEvent("bracketNavClicked"))
    } else {
      sessionStorage.removeItem("bracket:nav-clicked")
    }

    // Use native scrollIntoView - CSS scroll-margin handles all positioning
    el.scrollIntoView({ behavior: "smooth" })

    // Fallback alignment for dynamic content
    window.setTimeout(() => {
      if (!el || navLockRef.current !== null) return
      el.scrollIntoView({ behavior: "auto" })
    }, navConfig.scrollSettings.realignDelay)

    // Update URL hash
    if (window.history.replaceState) {
      window.history.replaceState(null, "", `${window.location.pathname}#${id}`)
    }

    // Brief lock to prevent observer conflicts during smooth scroll
    navLockRef.current = id
    setTimeout(() => {
      navLockRef.current = null
    }, navConfig.scrollSettings.navLockDuration)
  }

  return {
    activeSection,
    showNav,
    isInHeroSection,
    isInDemoSection,
    scrollToAnchor,
  }
}
